import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ACADEMY_URL, enroll, listTracks, TrackSummary } from '../../api/academy';
import { enrollLinked, isKitchenTenant } from '../../api/academyLink';
import { useAuthStore } from '../../store/authStore';
import { academyPath } from './academyRoute';
import { EnterKitchenButton } from './KitchenEntry';

// Self-serve PAI Academy enrollment, launched from the login screen (pre-auth
// by design: enrollment is what CREATES the learner's login). The password is
// emailed to the enrollee; only a platform operator enrolling someone else
// gets it back in the response, exactly once.

// Colors read --en-* variables with the fixed value as fallback: the modal is
// what it was unless an ancestor defines them, which only the sign-in page
// does under the new interface (pages/public.css, .pub-page).
const BRAND = 'var(--en-solid, var(--color-brand))';
const ON_BRAND = 'var(--en-on-solid, var(--color-on-brand))';

const inputStyle: React.CSSProperties = {
  width: '100%', height: 36, backgroundColor: 'var(--en-field, #1F2D3D)',
  border: '1px solid var(--en-border, #2D3F55)', borderRadius: 3, color: 'var(--en-text, #C5CDD8)',
  fontSize: 13, padding: '0 10px', outline: 'none', boxSizing: 'border-box',
};

const EnrollModal: React.FC<{
  onClose: () => void;
  /** Prefill when launched from inside the app (logged-in user). */
  defaultEmail?: string;
  defaultName?: string;
  /** In-app mode: the success copy explains the re-login, since the Kitchen
   *  is a separate account from the current session. */
  inApp?: boolean;
  /** Preselect a certification (the public catalog's "Inscribirme"). */
  defaultTrackKey?: string;
  /** After enrolling, the "Ir a iniciar sesión" button runs this instead of
   *  just closing (the catalog sends you to the sign-in page). */
  onSignIn?: () => void;
}> = ({ onClose, defaultEmail, defaultName, inApp, defaultTrackKey, onSignIn }) => {
  const { t } = useTranslation('academyLink');
  // A caller with a maic session outside any Kitchen enrolls with the identity
  // they already have (no account, no password). The public catalog has no
  // session bootstrap of its own, so ask the refresh cookie once.
  const sessionUser = useAuthStore((st) => st.user);
  const [sessionChecked, setSessionChecked] = useState(!!sessionUser);
  useEffect(() => {
    if (sessionUser) return;
    useAuthStore.getState().refresh().catch(() => undefined).finally(() => setSessionChecked(true));
  }, []);
  const linked = !!sessionUser && !isKitchenTenant(sessionUser.tenant_id) && !sessionUser.impersonated_by;
  const [linkedDone, setLinkedDone] = useState(false);
  const [tracks, setTracks] = useState<TrackSummary[]>([]);
  const [trackKey, setTrackKey] = useState('');
  const [name, setName] = useState(defaultName || '');
  const [email, setEmail] = useState(defaultEmail || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{
    email: string; password: string | null; loginCreated: boolean;
    credentialsSent: boolean; note?: string;
  } | null>(null);

  useEffect(() => {
    listTracks()
      .then((t) => {
        setTracks(t);
        const pre = defaultTrackKey && t.find((x) => x.key === defaultTrackKey);
        if (pre) setTrackKey(pre.key);
        else if (t.length) setTrackKey(t[0].key);
      })
      .catch(() => setError('No se pudo cargar el catálogo de certificaciones.'));
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (linked) {
      if (!trackKey) return;
      setBusy(true); setError('');
      try {
        await enrollLinked(trackKey);
        setLinkedDone(true);
      } catch (err: any) {
        setError(err?.response?.data?.detail || t('enroll.error'));
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!name.trim() || !email.trim() || !trackKey) {
      setError('Completa tu nombre, correo y certificación.');
      return;
    }
    setBusy(true); setError('');
    try {
      const res = await enroll({ track_key: trackKey, email: email.trim(), name: name.trim() });
      setResult({
        email: email.trim().toLowerCase(), password: res.password,
        loginCreated: res.login_created, credentialsSent: !!res.credentials_sent,
        note: res.note,
      });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'No se pudo completar la inscripción.');
    } finally {
      setBusy(false);
    }
  };

  const selected = tracks.find((t) => t.key === trackKey);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, backgroundColor: 'rgba(4, 8, 20, 0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 400, maxWidth: 'calc(100vw - 32px)', borderRadius: 6,
          backgroundColor: 'var(--en-card, #16202E)', border: '1px solid var(--en-border, #2D3F55)',
          padding: '22px 24px 24px', boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.09em', color: 'var(--en-eyebrow, #A78BFA)',
          textTransform: 'uppercase', marginBottom: 6,
        }}>
          PAI Academy
        </div>

        {linkedDone ? (
          <>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--en-title, var(--color-text-subtle))', marginBottom: 8 }}>
              {t('enroll.doneTitle')}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--color-text-subtle)', lineHeight: 1.6, marginBottom: 16 }}>
              {t('enroll.doneBody')}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <EnterKitchenButton target="/academy" labelKey="enroll.enter" />
              <button type="button" onClick={onClose} style={{
                height: 34, padding: '0 14px', borderRadius: 4, border: '1px solid var(--color-border)',
                backgroundColor: 'transparent', color: 'var(--color-text-subtle)', fontSize: 13, cursor: 'pointer',
              }}>
                {t('enroll.later')}
              </button>
            </div>
          </>
        ) : result ? (
          <>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--en-title, #F8FAFC)', marginBottom: 8 }}>
              {result.password ? 'Kitchen listo — guarda tus credenciales' : 'Kitchen listo'}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--color-text-subtle)', lineHeight: 1.6, marginBottom: 14 }}>
              Tu entorno personal fue creado
              {result.password
                ? ' y esta es tu contraseña. Se muestra UNA sola vez.'
                : result.credentialsSent
                  ? '. Te enviamos la contraseña a tu correo.'
                  : result.loginCreated
                    ? '. No pudimos enviarte la contraseña por correo: pide acceso a un administrador de PAI Academy.'
                    : '. Entra con la contraseña que ya tenías.'}
              {result.note ? ` ${result.note}` : ''}
              {inApp && ' Tu Kitchen es una cuenta separada: cierra sesión y vuelve a entrar con este usuario para empezar.'}
            </div>
            <div style={{
              backgroundColor: 'var(--en-code, #0F1824)', border: '1px solid var(--en-border, #2D3F55)', borderRadius: 4,
              padding: '12px 14px', fontFamily: 'monospace', fontSize: 12.5,
              color: 'var(--en-text, #C5CDD8)', marginBottom: 16, userSelect: 'all',
            }}>
              <div>Usuario: {result.email}</div>
              {result.password && <div data-academy="enroll-password">Contraseña: {result.password}</div>}
            </div>
            <button
              onClick={!inApp && onSignIn ? onSignIn : onClose}
              data-academy="enroll-done"
              style={{
                width: '100%', height: 36, borderRadius: 4, border: 'none',
                backgroundColor: BRAND, color: ON_BRAND, fontSize: 13, fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {inApp ? 'Entendido' : 'Ir a iniciar sesión'}
            </button>
          </>
        ) : (
          <form onSubmit={submit}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--en-title, #F8FAFC)', marginBottom: 8 }}>
              Certifícate construyendo de verdad
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--color-text-subtle)', lineHeight: 1.6, marginBottom: 16 }}>
              La inscripción crea tu Kitchen: un entorno real y personal. Las lecciones
              son a tu ritmo y el examen se califica contra lo que construyas — no es
              de opción múltiple.
            </div>

            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: 'var(--en-label, #7E8CA3)', marginBottom: 4 }}>
              Certificación
            </label>
            <select
              value={trackKey}
              onChange={(e) => setTrackKey(e.target.value)}
              data-academy="enroll-track"
              style={{ ...inputStyle, marginBottom: 4 }}
            >
              {tracks.map((t) => (
                <option key={t.key} value={t.key}>{t.title} (v{t.version})</option>
              ))}
            </select>
            {selected && (
              <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)', margin: '2px 0 12px', lineHeight: 1.5 }}>
                {selected.description} · {selected.lessons} lecciones + receta + examen
                {' · '}
                <a
                  href={`${ACADEMY_URL}/academy/tracks/${selected.key}/guide.pdf`}
                  target="_blank" rel="noreferrer"
                  style={{ color: 'var(--color-brand-border)' }}
                >
                  libro de estudio
                </a>
                {' · '}
                <a
                  href={academyPath({ view: 'catalog', track: selected.key })}
                  target="_blank" rel="noreferrer"
                  data-academy="enroll-syllabus"
                  style={{ color: 'var(--color-brand-border)' }}
                >
                  temario
                </a>
              </div>
            )}

            {linked && sessionUser ? (
              <div data-academy="enroll-linked" style={{
                fontSize: 12.5, color: 'var(--color-text-subtle)', lineHeight: 1.6, margin: '8px 0 16px',
              }}>
                <p style={{ margin: '0 0 8px', fontWeight: 600 }}>
                  {t('enroll.linkedAs', { name: sessionUser.name, email: sessionUser.email })}
                </p>
                <p style={{ margin: 0 }}>{t('enroll.linkedHow')}</p>
              </div>
            ) : !sessionChecked ? (
              <div style={{ fontSize: 12.5, color: 'var(--color-text-muted)', margin: '8px 0 16px' }}>{t('enroll.checking')}</div>
            ) : (
              <>
            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: 'var(--en-label, #7E8CA3)', marginBottom: 4 }}>
              Nombre completo
            </label>
            <input
              value={name} onChange={(e) => setName(e.target.value)}
              data-academy="enroll-name" placeholder="Cómo aparecerá en tu certificado"
              style={{ ...inputStyle, marginBottom: 12 }}
            />

            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: 'var(--en-label, #7E8CA3)', marginBottom: 4 }}>
              Correo
            </label>
            <input
              value={email} onChange={(e) => setEmail(e.target.value)} type="email"
              data-academy="enroll-email" placeholder="tu@correo.com"
              style={{ ...inputStyle, marginBottom: 4 }}
            />
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '0 0 16px', lineHeight: 1.5 }}>
              Si tu correo ya tiene cuenta en la plataforma, usa una variante como
              nombre+pai@tudominio — llega al mismo buzón.
            </div>

              </>
            )}

            {error && (
              <div style={{ fontSize: 12, color: 'var(--en-error, #F87171)', marginBottom: 12 }}>{error}</div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button" onClick={onClose}
                style={{
                  flex: 1, height: 36, borderRadius: 4, border: '1px solid var(--en-border, #2D3F55)',
                  backgroundColor: 'transparent', color: 'var(--color-text-subtle)', fontSize: 13,
                  fontWeight: 600, cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
              <button
                type="submit" disabled={busy || !sessionChecked}
                data-academy="enroll-submit"
                style={{
                  flex: 2, height: 36, borderRadius: 4, border: 'none',
                  backgroundColor: BRAND, color: ON_BRAND, fontSize: 13, fontWeight: 600,
                  cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1,
                }}
              >
                {busy ? t('enroll.busy') : t('enroll.submit')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EnrollModal;
